import { generatePath, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import { Button, DetailField, PageBreadcrumb, Spinner, Tag, Tooltip } from "@/modules/common/components";
import useNavigateBack from "@/modules/common/hooks/useNavigateBack";
import useToastOnError from "@/modules/common/hooks/useToastOnError";
import {
  MANAGER_USER_ROLE_OPTIONS,
  MANAGER_USER_STATUS_OPTIONS,
  ManagerUserRole,
  ManagerUserStatus,
} from "@/modules/common/types/ManagerUsers.enum";
import { formatDate, formatValue } from "@/modules/common/utils/format";
import { ROUTES } from "@/routes/constants/routes";

import { MANAGER_DETAILS_TAB_PARAM, MANAGER_DETAILS_TABS } from "../../constants/ManagerDetailsTabs";

import useManagerUserDetails from "./hooks/useManagerUserDetails";

const ManagerUserDetails = () => {
  const { id, managerUserId } = useParams();

  const companyRoute = generatePath(ROUTES.MANAGER.DETAILS, { id: id! });
  const companyUsersRoute = `${companyRoute}?${MANAGER_DETAILS_TAB_PARAM}=${MANAGER_DETAILS_TABS.USERS}`;
  const navigateBack = useNavigateBack(companyUsersRoute);

  const {
    data: user,
    isLoading,
    isError,
    error,
  } = useManagerUserDetails({ id: id!, managerUserId: managerUserId!, enabled: !!id && !!managerUserId });

  useToastOnError(isError, error, "Erro ao carregar usuário.", navigateBack);

  const isAdmin = user?.role === ManagerUserRole.ADMIN;
  const isActive = user?.status === ManagerUserStatus.ACTIVE;
  const statusLabel = MANAGER_USER_STATUS_OPTIONS.find((option) => option.value === String(user?.status))?.label ?? "-";
  const roleLabel = MANAGER_USER_ROLE_OPTIONS.find((option) => option.value === String(user?.role))?.label ?? "-";

  return (
    <div className="flex h-full flex-col gap-4">
      <PageBreadcrumb
        items={[
          { label: "Gestoras", to: ROUTES.MANAGER.LIST },
          ...(user
            ? [{ label: user.managementCompany.name, to: companyRoute }, { label: "Usuários", to: companyUsersRoute }, { label: user.name }]
            : []),
        ]}
      />
      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      ) : user ? (
        <>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col">
              <Tooltip label={user.name}>
                <p className="text-[22px] font-bold text-ui-primary truncate md:line-clamp-2 md:whitespace-normal">{user.name}</p>
              </Tooltip>
              <p className="h-5 text-[14px] text-ui-text-muted">{[user.email, user.managementCompany.name].join(" · ")}</p>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button variant="outlined" label="Voltar" icon={<ArrowLeft />} className="w-full sm:w-auto" onClick={navigateBack} />
            </div>
          </div>

          <div className="flex flex-col gap-4 rounded-xl bg-white p-4">
            <div className="flex flex-col">
              <p className="text-[16px] font-bold text-ui-primary">Dados Gerais</p>
              <p className="text-[12px] text-ui-text-muted">Informações cadastrais do usuário da gestora.</p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <DetailField label="Nome">{user.name}</DetailField>
              <DetailField label="E-mail">{user.email}</DetailField>

              <DetailField label="Telefone">{user.phoneNumber ? formatValue("phoneNumber", user.phoneNumber) : null}</DetailField>
              <DetailField label="Perfil">
                <Tag label={roleLabel} variant={isAdmin ? "primary" : "neutral"} className="border-0 py-0" />
              </DetailField>

              <DetailField label="Status">
                <Tag label={statusLabel} variant={isActive ? "success" : "neutral"} className="border-0 py-0" />
              </DetailField>
              <DetailField label="Representante da gestora">{user.isOwner ? "Sim" : "Não"}</DetailField>

              <DetailField label="Último acesso">
                {user.lastLoginAt ? formatDate(user.lastLoginAt, "DD/MM/YYYY [às] HH:mm") : "Nunca acessou"}
              </DetailField>
              <DetailField label="Criado em">{formatDate(user.createdAt, "DD/MM/YYYY [às] HH:mm")}</DetailField>

              <DetailField label="Atualizado em">{formatDate(user.updatedAt, "DD/MM/YYYY [às] HH:mm")}</DetailField>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default ManagerUserDetails;
